import classNames from 'classnames'
import {gsap} from 'gsap'
import {DrawSVGPlugin} from 'gsap/DrawSVGPlugin'
import * as React from 'react'
import {render} from 'storyblok-rich-text-react-renderer'

import IconArrowRight from '@/assets/specify/IconArrowRight'
import {makeEditable, unwrapAllNodeResolvers} from '@/common/utils'
import Content from '@/components/Content'
import {Grid} from '@/components/Grid'
import Heading from '@/components/Heading'
import Image from '@/components/Image'
import Link from '@/components/Link'

if (typeof window !== `undefined`) {
  gsap.registerPlugin(DrawSVGPlugin)
}

const Features = ({backgroundColors, headingTag, heading, content, items, _editable}) => {
  const HeadingTag = headingTag || 'p'
  const containerRef = React.useRef()
  const doodleRef = React.useRef(null)

  React.useEffect(() => {
    const container = containerRef.current

    if (container && doodleRef.current !== null) {
      const context = gsap.context(() => {
        gsap.from(doodleRef.current, {
          drawSVG: '0% 0%',
          scrollTrigger: {
            trigger: container,
            start: 'top 50%',
          },
          duration: 2,
        })
      })

      return () => context.revert()
    }
  })

  return (
    <section
      ref={containerRef}
      {...makeEditable(_editable)}
      className={classNames('py-32 lg:py-34 lg:pb-26', backgroundColors.current)}
    >
      <Grid className='grid-flow-dense md:gap-x-8'>
        <div className='col-span-full mb-8 md:col-start-1 md:col-end-6 lg:col-end-9'>
          <div className='lg:w-3/4'>
            <HeadingTag className='mb-6 space-y-6'>
              <Heading heading={heading} />
            </HeadingTag>
            <Content content={content} />
          </div>
        </div>

        {items.map((item, i) => {
          return item.link.url || item.link.story?.full_slug ? (
            <Link
              key={item._uid}
              link={item.link}
              className={classNames(
                'group relative col-span-full mb-8 block rounded-2xl p-6 pb-56 md:col-span-3 lg:col-span-4',
                {
                  'bg-turquoise-400': item.backgroundColor === 'turquoise',
                  'bg-violet-400': item.backgroundColor === 'purple',
                  'bg-yellow-400': item.backgroundColor === 'yellow',
                  'md:col-span-6 lg:col-span-8': item.isHighlighted,
                }
              )}
            >
              {i === 0 && (
                <svg
                  width='148'
                  height='376'
                  viewBox='0 0 148 376'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='pointer-events-none absolute -left-1/4 -top-1/3 block stroke-turquoise-400 opacity-0 lg:opacity-100'
                >
                  <path
                    d='M0.0429036 168.9C0.198982 168.394 0.735073 168.111 1.2403 168.267L67.3041 188.676C67.8093 188.832 68.0924 189.368 67.9363 189.874C67.7802 190.379 67.2441 190.662 66.7389 190.506L0.675089 170.097C0.169865 169.941 -0.113174 169.405 0.0429036 168.9ZM43.199 51.8419C43.4489 51.3759 44.0293 51.2007 44.4953 51.4506C46.6613 52.6122 49.69 55.3439 53.1097 58.882C56.5603 62.4522 60.4994 66.9407 64.5008 71.7196C72.505 81.279 80.7929 92.041 85.982 99.0356C86.297 99.4603 86.2082 100.06 85.7835 100.375C85.3588 100.69 84.7591 100.601 84.4441 100.177C79.2706 93.203 71.0064 82.472 63.0326 72.9489C59.045 68.1866 55.1387 63.7367 51.7328 60.2129C48.296 56.657 45.4573 54.1394 43.5903 53.1382C43.1243 52.8883 42.9491 52.3079 43.199 51.8419ZM139.788 81.9797C139.788 82.5085 140.217 82.9372 140.746 82.9372C141.275 82.9372 141.703 82.5085 141.703 81.9797L141.703 30.9573C141.703 30.4285 141.275 29.9998 140.746 29.9998C140.217 29.9998 139.788 30.4285 139.788 30.9573L139.788 81.9797ZM49.0043 369.337C48.8414 368.834 49.1171 368.294 49.6202 368.131C49.8687 368.051 50.3132 367.793 50.9642 367.279C51.591 366.785 52.3343 366.113 53.1688 365.297C54.8363 363.666 56.8184 361.505 58.8828 359.136C63.0092 354.403 67.4202 348.893 70.2211 345.265C70.5442 344.847 71.1455 344.769 71.5641 345.092C71.9826 345.416 72.06 346.017 71.7369 346.435C68.9208 350.083 64.485 355.624 60.3262 360.395C58.2481 362.779 56.228 364.983 54.5078 366.666C53.6484 367.506 52.8514 368.229 52.1508 368.782C51.4743 369.316 50.8042 369.761 50.2101 369.953C49.7071 370.116 49.1672 369.84 49.0043 369.337ZM7.26342 287.463C6.78207 287.682 6.56933 288.249 6.78824 288.731C7.00716 289.212 7.57482 289.425 8.05616 289.206L54.9711 267.869C55.4524 267.65 55.6651 267.083 55.4462 266.601C55.2273 266.12 54.6596 265.907 54.1783 266.126L7.26342 287.463Z'
                    strokeWidth='2'
                    strokeLinecap='round'
                    vectorEffect='non-scaling-stroke'
                    ref={doodleRef}
                  />
                </svg>
              )}

              <div className={classNames({'md:w-[38%]': item.isHighlighted})}>
                <div className='heading-s-millik break-words text-purple-900'>
                  {render(item.heading, {nodeResolvers: unwrapAllNodeResolvers})}
                </div>
                <div className='body-s-medium text-purple-900'>
                  {render(item.content, {nodeResolvers: unwrapAllNodeResolvers})}
                </div>
              </div>
              <div
                className={classNames('absolute bottom-0', {
                  'left-0 w-full max-w-[310px] md:left-auto md:right-20 md:max-h-[356px] md:w-1/2 md:max-w-[70%] lg:max-h-none':
                    item.isHighlighted,
                  'left-0  w-full max-w-[310px] md:max-w-[70%]': !item.isHighlighted,
                })}
              >
                <div
                  className={classNames(
                    {'aspect-h-3 aspect-w-4': !item.isHighlighted},
                    {
                      'aspect-h-3 aspect-w-4 md:aspect-h-1 md:aspect-w-1': item.isHighlighted,
                    }
                  )}
                >
                  <Image
                    {...item.image}
                    className='scale-100 object-bottom transition-all duration-100 ease-in group-hover:scale-105'
                    sizes={{sm: 70, md: 60, lg: item.isHighlighted ? 50 : 30}}
                    ratio={item.isHighlighted ? '1:1' : '4:3'}
                    fitIn
                  />
                </div>
              </div>

              <div className='absolute bottom-4 right-4 flex h-14 w-14 items-center justify-center rounded-full border transition duration-300 group-hover:bg-purple-900'>
                <div className='inline-block transition-transform duration-300 group-hover:rotate-45 group-hover:text-white'>
                  <IconArrowRight />
                </div>
              </div>
            </Link>
          ) : (
            <div
              key={item._uid}
              className={classNames(
                'group relative col-span-full mb-8 block rounded-2xl p-6 pb-56 md:col-span-3 lg:col-span-4',
                {
                  'bg-turquoise-400': item.backgroundColor === 'turquoise',
                  'bg-violet-400': item.backgroundColor === 'purple',
                  'bg-yellow-400': item.backgroundColor === 'yellow',
                  'md:col-span-6 lg:col-span-8': item.isHighlighted,
                }
              )}
            >
              {i === 0 && (
                <svg
                  width='148'
                  height='376'
                  viewBox='0 0 148 376'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='pointer-events-none absolute -left-1/4 -top-1/3 block stroke-turquoise-400 opacity-0 lg:opacity-100'
                >
                  <path
                    d='M0.0429036 168.9C0.198982 168.394 0.735073 168.111 1.2403 168.267L67.3041 188.676C67.8093 188.832 68.0924 189.368 67.9363 189.874C67.7802 190.379 67.2441 190.662 66.7389 190.506L0.675089 170.097C0.169865 169.941 -0.113174 169.405 0.0429036 168.9ZM43.199 51.8419C43.4489 51.3759 44.0293 51.2007 44.4953 51.4506C46.6613 52.6122 49.69 55.3439 53.1097 58.882C56.5603 62.4522 60.4994 66.9407 64.5008 71.7196C72.505 81.279 80.7929 92.041 85.982 99.0356C86.297 99.4603 86.2082 100.06 85.7835 100.375C85.3588 100.69 84.7591 100.601 84.4441 100.177C79.2706 93.203 71.0064 82.472 63.0326 72.9489C59.045 68.1866 55.1387 63.7367 51.7328 60.2129C48.296 56.657 45.4573 54.1394 43.5903 53.1382C43.1243 52.8883 42.9491 52.3079 43.199 51.8419ZM139.788 81.9797C139.788 82.5085 140.217 82.9372 140.746 82.9372C141.275 82.9372 141.703 82.5085 141.703 81.9797L141.703 30.9573C141.703 30.4285 141.275 29.9998 140.746 29.9998C140.217 29.9998 139.788 30.4285 139.788 30.9573L139.788 81.9797ZM49.0043 369.337C48.8414 368.834 49.1171 368.294 49.6202 368.131C49.8687 368.051 50.3132 367.793 50.9642 367.279C51.591 366.785 52.3343 366.113 53.1688 365.297C54.8363 363.666 56.8184 361.505 58.8828 359.136C63.0092 354.403 67.4202 348.893 70.2211 345.265C70.5442 344.847 71.1455 344.769 71.5641 345.092C71.9826 345.416 72.06 346.017 71.7369 346.435C68.9208 350.083 64.485 355.624 60.3262 360.395C58.2481 362.779 56.228 364.983 54.5078 366.666C53.6484 367.506 52.8514 368.229 52.1508 368.782C51.4743 369.316 50.8042 369.761 50.2101 369.953C49.7071 370.116 49.1672 369.84 49.0043 369.337ZM7.26342 287.463C6.78207 287.682 6.56933 288.249 6.78824 288.731C7.00716 289.212 7.57482 289.425 8.05616 289.206L54.9711 267.869C55.4524 267.65 55.6651 267.083 55.4462 266.601C55.2273 266.12 54.6596 265.907 54.1783 266.126L7.26342 287.463Z'
                    strokeWidth='2'
                    strokeLinecap='round'
                    vectorEffect='non-scaling-stroke'
                    ref={doodleRef}
                  />
                </svg>
              )}

              <div className={classNames({'md:w-[38%]': item.isHighlighted})}>
                <div className='heading-s-millik break-words text-purple-900'>
                  {render(item.heading, {nodeResolvers: unwrapAllNodeResolvers})}
                </div>
                <div className='body-s-medium text-purple-900'>
                  {render(item.content, {nodeResolvers: unwrapAllNodeResolvers})}
                </div>
              </div>
              <div
                className={classNames('absolute bottom-0', {
                  'left-0 w-full max-w-[310px] md:left-auto md:right-20 md:max-h-[356px] md:w-1/2 md:max-w-[70%] lg:max-h-none':
                    item.isHighlighted,
                  'left-0  w-full max-w-[310px] md:max-w-[70%]': !item.isHighlighted,
                })}
              >
                <div
                  className={classNames(
                    {'aspect-h-3 aspect-w-4': !item.isHighlighted},
                    {
                      'aspect-h-3 aspect-w-4 md:aspect-h-1 md:aspect-w-1': item.isHighlighted,
                    }
                  )}
                >
                  <Image
                    {...item.image}
                    className='object-bottom'
                    sizes={{sm: 70, md: 60, lg: item.isHighlighted ? 50 : 30}}
                    ratio={item.isHighlighted ? '1:1' : '4:3'}
                    fitIn
                  />
                </div>
              </div>
            </div>
          )
        })}
      </Grid>
    </section>
  )
}

export default Features
